import Constants from '@/Constants';
import { CourseLessonContentTO } from '@/models/CourseLessonContentTO';
import { Lesson } from '@/models/Lesson';
import { LessonEnrollment } from '@/models/LessonEnrollment';
import List from '@/models/List';
import { CourseViewType, ViewType } from '@/models/ViewType';
import { AnyAction, Reducer } from 'redux';
import { PURGE } from 'redux-persist';
import _ from 'underscore';
import { CourseStudentView, ListCourseStudentViewData } from '../list/types';
import { Lists, ListsState, ListTypes } from './types';

const initialStateCourseList: ListCourseStudentViewData = {
    list: [] as CourseStudentView[],
    total: 0,
    totalToExpire: 0,
    loading: true,
};

const INITIAL_STATE: ListsState = {
    data: {} as Lists,
    courseData: {} as CourseLessonContentTO,
    list: undefined,
    refreshList: undefined,
    listData: {
        listLastPublishedStudentView: { ...initialStateCourseList },
        listMostAccessedStudentView: { ...initialStateCourseList },
        listInProgressStudentView: { ...initialStateCourseList },
        listCompletedStudentView: { ...initialStateCourseList },
        listTopRatedStudentView: { ...initialStateCourseList },
        listFavoriteStudentView: { ...initialStateCourseList },
        listLikeYouStudentView: { ...initialStateCourseList },
        listForYouStudentView: { ...initialStateCourseList },
        listCoursesHavingAContentStudentView: { ...initialStateCourseList },
        listSearchResultStudentView: { ...initialStateCourseList },
        listMyCourseStudentView: { ...initialStateCourseList },
        listEssentialStudentView: { ...initialStateCourseList },
        listPublishedUserContentsStudentView: { ...initialStateCourseList },
        listTrainingsEnrolledStudentView: { ...initialStateCourseList },
    },
    currentData: {} as CourseStudentView,
    loading: true,
    hasError: false,
    error: undefined,
    success: false,
    successOnCreateCourse: false,
    successOnUpdateScorm: false,
    hasErrorOnUpdateScorm: false,
    isUserCreatingList: false,
    isUserAssociatingList: false,
    selectedCourseForInsertion: null,
    scormPackageStatus: undefined,
    successOnUpdateCourseStudentView: false,
    successOnPublishList: false,
    successOnArchiveList: false,
    successOnUpdateList: false,
    hasErrorOnUpdate: false,
    hasErrorOnArchive: false,
};

const removeCourseFromList = (state: ListsState, action: AnyAction, listName: ViewType, courseId: number) => {
    const currentList: CourseStudentView[] = [...state.listData[listName].list];
    const resultList: CourseStudentView[] = currentList.filter((item: CourseStudentView) => item.course.id !== courseId);
    return {
        ...state,
        loading: false,
        hasError: false,
        listData: {
            ...state.listData,
            [listName]: {
                ...state.listData[listName],
                list: resultList,
                loading: false,
            },
        },
    };
};

const processCourseRequestSuccess = (state: ListsState, action: AnyAction, listName: ViewType | CourseViewType) => {
    const currentList: CourseStudentView[] = [...state.listData[listName].list];
    const newList: CourseStudentView[] = [...action.payload.data].filter(
        (fetchedCourseView) =>
            currentList.findIndex(
                (currentCourseView) =>
                    currentCourseView.course.id === fetchedCourseView.course.id &&
                    currentCourseView.classEnrollment.id === fetchedCourseView.classEnrollment.id,
            ) === -1,
    );
    const mergedlist: CourseStudentView[] = currentList.concat(newList);

    return {
        ...state,
        loading: false,
        hasError: false,
        listData: {
            ...state.listData,
            [listName]: {
                ...state.listData[listName],
                list: mergedlist,
                total: action.payload.total || 0,
                totalToExpire: action.payload.totalToExpire || 0,
                loading: false,
            },
        },
    };
};

const updateScormPackageStatus = (state: ListsState, action: AnyAction) => {
    if (
        !state.scormPackageStatus ||
        state.scormPackageStatus.created !== action.payload.created ||
        state.scormPackageStatus.updated !== action.payload.updated
    ) {
        return { ...state, hasError: false, scormPackageStatus: action.payload };
    }
    return state;
};

const reducer: Reducer<ListsState> = (state: ListsState = INITIAL_STATE, action) => {
    switch (action.type) {
        case PURGE:
            return { ...INITIAL_STATE };
        case 'persist/REHYDRATE':
            return { ...state, selectedCourseForInsertion: {} as List };
        case ListTypes.GET_LIST_REQUEST:
            return { ...state, loading: true };
        case ListTypes.GET_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                list: action.payload,
            };
        case ListTypes.GET_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case ListTypes.GET_LISTS_REQUEST:
            return { ...state, loading: true };
        case ListTypes.GET_LISTS_SUCCESS_FECTH_MORE:
            return {
                ...state,
                loading: false,
                data: { ...state.data, lists: state.data.lists ? state.data.lists.concat(action.payload) : action.payload },
            };
        case ListTypes.GET_LISTS_SUCCESS:
            return {
                ...state,
                loading: false,
                data: { lists: action.payload.data, total: action.payload.total },
                total: action.payload.total,
            };
        case ListTypes.GET_LISTS_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case ListTypes.POST_LIST_REQUEST:
            return { ...state, loading: true };
        case ListTypes.POST_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
                data: {
                    ...state.data,
                    lists: { ...state.data.lists, ...action.payload },
                },
            };
        case ListTypes.POST_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case ListTypes.PUT_LIST_REQUEST:
            return { ...state, loading: true };
        case ListTypes.PUT_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
            };
        case ListTypes.PUT_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                error: action.payload,
            };
        case ListTypes.DELETE_LIST_REQUEST:
            return { ...state, loading: true, refreshList: false };
        case ListTypes.DELETE_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                refreshList: true,
            };
        case ListTypes.DELETE_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
            };
        case ListTypes.PUBLISH_LIST_REQUEST:
            return { ...state, loading: true, successOnPublishList: false };
        case ListTypes.PUBLISH_LIST_SUCCESS:
            return {
                ...state,
                courseData: { ...state.courseData, status: 'PUBLISHED' },
                loading: false,
                successOnPublishList: true,
            };
        case ListTypes.PUBLISH_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                successOnPublishList: false,
            };
        case ListTypes.ARCHIVE_LIST_REQUEST:
            return { ...state, hasError: false, loading: true, successOnArchiveList: false, hasErrorOnArchive: false };
        case ListTypes.ARCHIVE_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                hasError: false,
                successOnArchiveList: true,
                hasErrorOnArchive: false,
            };
        case ListTypes.ARCHIVE_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
                successOnArchiveList: false,
                hasErrorOnArchive: true,
            };
        case ListTypes.TOGGLE_LIST_VISIBILITY_SUCCESS:
            return {
                ...state,
                loading: false,
                currentData: {
                    ...state.currentData,
                    course: { ...state.currentData.course, visibility: !state.currentData.course?.visibility, visibilityDate: new Date().getTime() },
                },
                data: {
                    ...state.data,
                    lists: state.data.lists?.map((item) => {
                        if (item.id === action.payload) item.visibility = !item.visibility;
                        return item;
                    }),
                },
            };
        case ListTypes.TOGGLE_LIST_VISIBILITY_FAILURE:
            return {
                ...state,
                loading: false,
                hasError: true,
            };
        case ListTypes.TOGGLE_LIST_VISIBILITY_CLEAR:
            return {
                ...state,
                hasError: false,
            };
        case ListTypes.ARCHIVE_LIST_FAILURE:
            return {
                ...state,
                loading: false,
                error: action.payload,
                hasError: true,
            };
        case ListTypes.ARCHIVE_LIST_CLEAR:
        case ListTypes.PUBLISH_LIST_CLEAR:
            return {
                ...state,
                successOnPublishList: false,
            };
        case ListTypes.GET_LISTS_CLEAR:
        case ListTypes.GET_LIST_CLEAR:
        case ListTypes.GET_COURSE_CLEAR:
        case ListTypes.POST_LIST_CLEAR:
        case ListTypes.PUT_LIST_CLEAR:
        case ListTypes.COURSES_CLEAR:
            return INITIAL_STATE;
        case ListTypes.LAST_PUBLISHED_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listLastPublishedStudentView');
        case ListTypes.MOST_ACCESSED_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listMostAccessedStudentView');
        case ListTypes.IN_PROGRESS_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listInProgressStudentView');
        case ListTypes.COMPLETED_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listCompletedStudentView');
        case ListTypes.TOP_RATED_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listTopRatedStudentView');
        case ListTypes.FAVORITES_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listFavoriteStudentView');
        case ListTypes.FOR_YOU_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listForYouStudentView');
        case ListTypes.LIKE_YOU_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listLikeYouStudentView');
        case ListTypes.SEARCHED_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listSearchResultStudentView');
        case ListTypes.COURSES_HAVING_A_CONTENT_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listCoursesHavingAContentStudentView');
        case ListTypes.MY_COURSES_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listMyCourseStudentView');
        case ListTypes.REMOVE_COURSE_FROM_LIST:
            return removeCourseFromList(state, action, 'listFavoriteStudentView', action.payload.courseId);
        case ListTypes.ESSENTIAL_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listEssentialStudentView');
        case ListTypes.PUBLISHED_USER_CONTENTS_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listPublishedUserContentsStudentView');
        case ListTypes.CATEGORIZED_SUCCESS:
            const listName: ViewType = (Constants.listCategorizedPrefix + action.payload.categoryId) as ViewType;
            return processCourseRequestSuccess(state, action, listName);
        case ListTypes.ADD_LIST_BY_CATEGORY:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    [Constants.listCategorizedPrefix + action.payload.categoryId]: { ...initialStateCourseList },
                },
            };
        case ListTypes.COURSES_REQUEST:
            return {
                ...state,
                hasError: false,
            };
        case ListTypes.COURSES_HAVING_A_CONTENT_CLEAR:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listCoursesHavingAContentStudentView: { ...initialStateCourseList },
                },
            };
        case ListTypes.MY_COURSES_CLEAR:
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listMyCourseStudentView: { ...initialStateCourseList },
                },
            };
        case ListTypes.FINISH_LESSON_REQUEST:
            return { ...state, hasError: false };
        case ListTypes.FINISH_LESSON_SUCCESS:
            return { ...state, loading: false, hasError: false };
        case ListTypes.FINISH_LESSON_FAILURE:
            return { ...state, loading: false, hasError: true };
        case ListTypes.UPDATE_COURSE_STUDENT_VIEW_REQUEST:
            return { ...state, successOnUpdateCourseStudentView: false, hasError: false };
        case ListTypes.UPDATE_COURSE_STUDENT_VIEW_SUCCESS:
            _(state.listData)
                .chain()
                .toArray()
                .pluck('list')
                .flatten()
                .compact()
                .filter(
                    (courseStudentView: CourseStudentView) =>
                        courseStudentView.course.id === action.payload.course.id &&
                        courseStudentView.classEnrollment.id === action.payload.classEnrollment.id,
                )
                .each((courseStudentView: CourseStudentView) => {
                    courseStudentView.courseEnrollment = action.payload.courseEnrollment;
                    courseStudentView.course.rating = action.payload.course.rating;
                    courseStudentView.course.totalRating = action.payload.course.totalRating;
                })
                .value();
            return {
                ...state,
                currentData: {
                    ...state.currentData,
                    courseEnrollment: action.payload.courseEnrollment,
                    course: {
                        ...state.currentData.course,
                        rating: action.payload.course.rating,
                        totalRating: action.payload.course.totalRating,
                    },
                },
                loading: false,
                successOnUpdateCourseStudentView: true,
            };
        case ListTypes.UPDATE_CONTENT_STUDENT_VIEW_IN_COURSES:
            _(state.listData)
                .chain()
                .toArray()
                .pluck('list')
                .flatten()
                .compact()
                .each((courseStudentView: CourseStudentView) => {
                    if (courseStudentView.course.lessons) {
                        courseStudentView.course.lessons
                            .filter((l: Lesson) => l.content.id === action.payload.content.id)
                            .forEach((lessonToUpdate: Lesson) => {
                                lessonToUpdate.content.rating = action.payload.content.rating;
                                lessonToUpdate.content.totalRating = action.payload.content.totalRating;
                            });
                    }
                })
                .value();
            return { ...state, loading: false, hasError: false };
        case ListTypes.GET_COURSE_STUDENT_VIEW_REQUEST:
            return { ...state, success: false, loading: true, hasError: false };
        case ListTypes.GET_COURSE_STUDENT_VIEW_SUCCESS:
            return { ...state, success: true, currentData: { ...action.payload }, loading: false, hasError: false };
        case ListTypes.GET_COURSE_STUDENT_VIEW_FAILURE:
            return { ...state, loading: false, success: false, hasError: true };
        case ListTypes.CLEAR_COURSE:
            return { ...state, success: false, currentData: {} as CourseStudentView };
        case ListTypes.COURSES_SEARCHED_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listSearchResultStudentView: { ...initialStateCourseList },
                },
            };
        }
        case ListTypes.CREATE_COURSE_REQUEST:
            return {
                ...state,
                loading: true,
                success: false,
                hasError: false,
                isUserCreatingList: !!action.payload.ownerId,
                successOnCreateCourse: false,
            };
        case ListTypes.CREATE_COURSE_SUCCESS:
            return { ...state, loading: false, success: true, hasError: false, courseData: action.payload, successOnCreateCourse: true };
        case ListTypes.CREATE_COURSE_FAILURE:
            return { ...state, loading: false, success: false, hasError: true, successOnCreateCourse: false };
        case ListTypes.COURSES_FAILURE:
            return { ...state, loading: false, success: false, hasError: true };
        case ListTypes.CREATE_COURSE_RESET:
            return { ...state, isUserCreatingList: false, success: false, successOnCreateCourse: false, courseData: {} as CourseLessonContentTO };
        case ListTypes.UPDATE_COURSE_REQUEST:
            return { ...state, loading: true, success: false, hasError: false, successOnUpdateList: false, hasErrorOnUpdate: false };
        case ListTypes.UPDATE_COURSE_SUCCESS:
            return {
                ...state,
                loading: false,
                success: true,
                hasError: false,
                courseData: action.payload,
                successOnUpdateList: true,
                hasErrorOnUpdate: false,
            };
        case ListTypes.UPDATE_COURSE_FAILURE:
            return { ...state, loading: false, success: false, hasError: true, successOnUpdateList: false, hasErrorOnUpdate: true };
        case ListTypes.UPDATE_COURSE_RESET:
            return { ...state, success: false, courseData: {} as CourseLessonContentTO };
        case ListTypes.ASSOCIATE_COURSE_RESET:
            return { ...state, selectedCourseForInsertion: null, isUserAssociatingList: false };
        case ListTypes.SET_COURSE_FOR_INSERTION:
            return { ...state, selectedCourseForInsertion: action.payload };
        case ListTypes.ADD_CONTENT_TO_COURSE_REQUEST:
            return { ...state, loading: true, hasError: false, isUserAssociatingList: true };
        case ListTypes.ADD_CONTENT_TO_COURSE_SUCCESS:
            return { ...state, loading: false, success: true, isUserAssociatingList: true };
        case ListTypes.ADD_CONTENT_TO_COURSE_FAILURE:
            return { ...state, loading: false, error: action.payload, hasError: true, isUserAssociatingList: true };
        case ListTypes.GET_COURSE_REQUEST:
            return { ...state, loading: true };
        case ListTypes.GET_COURSE_SUCCESS:
            return { ...state, loading: false, success: true, hasError: false, courseData: action.payload };
        case ListTypes.GET_COURSE_FAILURE:
            return { ...state, loading: false, success: false, hasError: true, error: action.payload };
        case ListTypes.UPDATE_SCORM_PACKAGE_REQUEST:
            return { ...state, loading: true, successOnUpdateScorm: false, hasErrorOnUpdateScorm: false };
        case ListTypes.UPDATE_SCORM_PACKAGE_SUCCESS:
            return { ...state, loading: false, successOnUpdateScorm: true, hasErrorOnUpdateScorm: false };
        case ListTypes.UPDATE_SCORM_PACKAGE_FAILURE:
            return { ...state, loading: false, successOnUpdateScorm: false, hasErrorOnUpdateScorm: true };
        case ListTypes.GET_SCORM_PACKAGE_STATUS_REQUEST:
            return { ...state, hasError: false, scormPackageStatus: undefined };
        case ListTypes.GET_SCORM_PACKAGE_STATUS_SUCCESS:
            return updateScormPackageStatus(state, action);
        case ListTypes.GET_SCORM_PACKAGE_STATUS_FAILURE:
            return { ...state, hasError: true, scormPackageStatus: undefined };
        case ListTypes.GET_SCORM_PACKAGE_STATUS_CLEAR:
            return { ...state, hasError: false, scormPackageStatus: undefined };
        case ListTypes.COURSES_COMPLETED_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listCompletedStudentView: { ...initialStateCourseList },
                },
            };
        }
        case ListTypes.CLEAR_ARCHIVE_CONFIGURATION:
            return {
                ...state,
                success: false,
                hasError: false,
                loading: false,
                successOnArchiveList: false,
                hasErrorOnArchive: false,
            };
        case ListTypes.CLEAR_UPDATE_CONFIGURATION:
            return {
                ...state,
                success: false,
                hasError: false,
                loading: false,
                successOnUpdateList: false,
                hasErrorOnUpdate: false,
            };
        case ListTypes.PUBLISHED_USER_CONTENTS_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listPublishedUserContentsStudentView: { ...initialStateCourseList },
                },
            };
        }
        case ListTypes.START_ENROLLMENT_REQUEST: {
            return { ...state, loading: true, hasError: false };
        }
        case ListTypes.START_ENROLLMENT_SUCCESS: {
            const currentDataBkp: CourseStudentView = { ...state.currentData } as CourseStudentView;
            const newEvaluationLessonEnrollmentList: LessonEnrollment[] = action.payload.courseEnrollment?.lessonEnrollments?.filter(
                (le: LessonEnrollment) => le.type === 'EvaluationLessonEnrollment',
            );          

            newEvaluationLessonEnrollmentList.forEach((le: LessonEnrollment) => {
                const oldEvaluationLessonEnrollment: LessonEnrollment | undefined = currentDataBkp.courseEnrollment?.lessonEnrollments?.find(
                    (l: LessonEnrollment) => l.lesson.id === le.lesson.id,
                );                
                if (oldEvaluationLessonEnrollment) {
                    le.lesson = oldEvaluationLessonEnrollment.lesson;
                }
            });

            return { ...state, loading: false, hasError: false, currentData: action.payload };
        }
        case ListTypes.START_ENROLLMENT_FAILURE: {
            return { ...state, loading: false, hasError: true };
        }

        case ListTypes.TRAININGS_SUCCESS:
            return processCourseRequestSuccess(state, action, 'listTrainingsEnrolledStudentView');

        case ListTypes.TRAININGS_CLEAR: {
            return {
                ...state,
                listData: {
                    ...state.listData,
                    listTrainingsEnrolledStudentView: { ...initialStateCourseList },
                },
            };
        }
        default:
            return state;
    }
};

export default reducer;
